import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { PatternFormState } from '@/features/schema/types'
import type { EntityType, RelationType, ValidationFinding } from '@/services/api/schemas'
import { DialogValidationFindings } from './DialogValidationFindings'

export interface PatternFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isEditing: boolean
  form: PatternFormState
  onFormChange: (form: PatternFormState) => void
  entityTypes: EntityType[]
  relationTypes: RelationType[]
  onSave: () => void
  findings?: ValidationFinding[] | null
}

/**
 * Controlled create/edit dialog for an extraction pattern. Entity/relation
 * option lists are passed in as props so the dialog performs no IO.
 */
export function PatternFormDialog({
  open,
  onOpenChange,
  isEditing,
  form,
  onFormChange,
  entityTypes,
  relationTypes,
  onSave,
  findings,
}: PatternFormDialogProps) {
  const { t } = useTranslation()
  const previewReady = form.source_type && form.relationship_type && form.target_type

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[85dvh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-foreground">
            {t(`schemaAgent.editor.patternDialog.${isEditing ? 'titleEdit' : 'titleNew'}`)}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('schemaAgent.editor.patternDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-1">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.patternDialog.source')}
            </Label>
            <Select
              value={form.source_type}
              onValueChange={value => onFormChange({ ...form, source_type: value })}
            >
              <SelectTrigger className="max-md:w-full bg-muted border-border">
                <SelectValue
                  placeholder={t('schemaAgent.editor.patternDialog.sourcePlaceholder')}
                />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border z-[250]">
                {entityTypes.map(entity => (
                  <SelectItem key={entity.id} value={entity.label}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.patternDialog.relationship')}
            </Label>
            <Select
              value={form.relationship_type}
              onValueChange={value => onFormChange({ ...form, relationship_type: value })}
            >
              <SelectTrigger className="max-md:w-full bg-muted border-border">
                <SelectValue
                  placeholder={t('schemaAgent.editor.patternDialog.relationshipPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border z-[250]">
                {relationTypes.map(rel => (
                  <SelectItem key={rel.id} value={rel.label}>
                    {rel.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.patternDialog.target')}
            </Label>
            <Select
              value={form.target_type}
              onValueChange={value => onFormChange({ ...form, target_type: value })}
            >
              <SelectTrigger className="max-md:w-full bg-muted border-border">
                <SelectValue
                  placeholder={t('schemaAgent.editor.patternDialog.targetPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border z-[250]">
                {entityTypes.map(entity => (
                  <SelectItem key={entity.id} value={entity.label}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pattern Preview */}
          {previewReady && (
            <div className="flex max-md:flex-wrap items-center justify-center gap-2 p-3 bg-accent/50 rounded-lg border border-border">
              <Badge className="bg-info-surface text-info-foreground border-info-border">
                {form.source_type}
              </Badge>
              <ArrowRight className="h-4 w-4 text-warning-foreground" />
              <Badge className="bg-warning-surface text-warning-foreground border-warning-border font-mono text-xs">
                {form.relationship_type}
              </Badge>
              <ArrowRight className="h-4 w-4 text-warning-foreground" />
              <Badge className="bg-info-surface text-info-foreground border-info-border">
                {form.target_type}
              </Badge>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.descriptionOptional')}
            </Label>
            <Textarea
              value={form.description}
              onChange={e => onFormChange({ ...form, description: e.target.value })}
              placeholder={t('schemaAgent.editor.patternDialog.descriptionPlaceholder')}
              className="bg-muted border-border h-[100px] resize-none overflow-y-auto"
            />
          </div>
        </div>

        <DialogValidationFindings findings={findings} />

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSave}
            className="bg-success hover:bg-success/90"
            disabled={!form.source_type || !form.relationship_type || !form.target_type}
          >
            {isEditing
              ? t('schemaAgent.editor.saveChanges')
              : t('schemaAgent.editor.patternDialog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
